<script setup>
import { useCommonCodeStore } from '@/store/commoncode-store';
import { useReservationsStore } from '@/store/reservation-store';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
    contractType: {
        type: String,
        default: 'SHORT'
    },
    validMsg: {
        type: Object,
        default: () => ({})
    },
    requiredList: {
        type: [Array, Object],
        default: () => []
    },
    readonlyList: {
        type: [Array, Object],
        default: () => []
    },
    hideList: {
        type: [Array, Object],
        default: () => []
    },
    nonComInsurance: {
        type: Boolean,
        default: false
    },
    insuranceData: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update']);
const route = useRoute();
const store = useReservationsStore();
const sessionKey = `${route.name}`;
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
// const codes = computed(() => useCommonCodeStore().getCommonCodes)
// const getCommonCode = (code) => codes.value?.[code] ?? []
const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const comInsurance = ref(null);

const initialData = {
    collision_insurance_age: '', // 자차보험나이
    collision_insurance_state: 'NON', // 자차보험종류
    collision_coverage_type: '', // 자차한도 단위
    collision_insurance_coverage: '', // 자차한도
    bodily_injury_coverage: '', // 대인 보상 한도
    bodily_injury_excess: '', // 대인 자기부담금(대인면책금)
    property_damage_coverage: '', // 대물 보상 한도
    property_damage_excess: '', // 대물 자기부담금(대물면책금)
    personal_injury_coverage: '', // 개인 상해 보상 한도/자손한도(부상)
    personal_death_coverage: '', // 개인 사망 보상 한도/자손한도(사망)
    personal_injury_excess: '' // 개인 상해 자기부담금(자손면책금)
};

const data = reactive({ ...initialData });
const sessionRef = store.ensure(sessionKey);

const commonSyncKeys = computed(() => Object.keys(data).filter((k) => Object.prototype.hasOwnProperty.call(sessionRef, k)));
const pickKeys = (src, keys) =>
    keys.reduce((o, k) => {
        if (k in src) o[k] = src[k];
        return o;
    }, {});

let pushing = false;
let pulling = false;

const requiredData = [];

const requiredKeys = computed(() => {
    const res = props.requiredList;
    if (Array.isArray(res) && res.length) return res;
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return Object.keys(res).filter((key) => !!res[key]);
    }
    return requiredData;
});

const requiredKeySet = computed(() => new Set(requiredKeys.value));
const isRequired = (key) => requiredKeySet.value.has(key);

const initialValidMessage = {
    collision_insurance_age: '',
    collision_insurance_state: '',
    collision_coverage_type: '',
    collision_insurance_coverage: '',
    bodily_injury_coverage: '',
    bodily_injury_excess: '',
    property_damage_coverage: '',
    property_damage_excess: '',
    personal_injury_coverage: '',
    personal_death_coverage: '',
    personal_injury_excess: ''
};
const validationMessage = reactive({ ...initialValidMessage });

const propsReadonlyControlled = computed(() => {
    const res = props.readonlyList;
    if (Array.isArray(res)) return new Set(res);
    if (res && typeof res === 'object') return new Set(Object.keys(res));
    return new Set();
});

const allReadonly = ref(false);
const setAllReadonly = (val = true) => {
    allReadonly.value = !!val;
};
const allowedKeys = Object.keys(initialData);

const internalReadonlyKeys = computed(() => {
    const set = new Set();
    if (allReadonly.value) allowedKeys.forEach((k) => set.add(k));
    return set;
});

const hasState = computed(() => {
    const v = data.collision_insurance_state;
    return v !== '' && v !== 'NON';
});
const hasType = computed(() => data.collision_coverage_type !== '');

const domainReadonlyKeys = computed(() => {
    const set = new Set();

    if (props.contractType === 'SHORT') {
        set.add('collision_insurance_state');
        set.add('collision_insurance_age');

        if (!hasState.value) {
            set.add('collision_coverage_type');
            set.add('collision_insurance_coverage');
        } else {
            if (!hasType.value) set.add('collision_insurance_coverage');
        }
    } else {
        if (!hasState.value) {
            set.add('collision_insurance_age');
            set.add('collision_coverage_type');
            set.add('collision_insurance_coverage');
        } else if (!hasType.value) {
            set.add('collision_insurance_coverage');
        }
    }

    return set;
});

const propsReadonlyEnforced = computed(() => {
    const res = props.readonlyList;
    const set = new Set();
    if (Array.isArray(res)) res.forEach((k) => set.add(k));
    else if (res && typeof res === 'object') {
        Object.keys(res).forEach((k) => {
            if (res[k]) set.add(k);
        });
    }
    return set;
});

const finalReadonlySet = computed(() => {
    const enforced = propsReadonlyEnforced.value;
    const result = new Set(enforced);

    domainReadonlyKeys.value.forEach((k) => {
        if (!enforced.has(k)) result.add(k);
    });

    if (allReadonly.value) {
        allowedKeys.forEach((k) => {
            if (!enforced.has(k)) result.add(k);
        });
    }
    return result;
});

const isFieldReadonly = (key) => finalReadonlySet.value.has(key);
const isEffectivelyRequired = (key) => requiredKeySet.value.has(key) && !isFieldReadonly(key) && !isHidden(key);

const comInsuranceKey = ['bodily_injury_coverage', 'bodily_injury_excess', 'property_damage_coverage', 'property_damage_excess', 'personal_injury_coverage', 'personal_death_coverage', 'personal_injury_excess'];

const isColInsuranceActionDisabled = computed(() => {
    if (allReadonly.value) return true;
    if (props.contractType === 'SHORT') {
        const enforced = propsReadonlyEnforced.value;
        return enforced.has('collision_insurance_state') && enforced.has('collision_insurance_age');
    }
    return false;
});
const isComInsuranceActionDisabled = computed(() => {
    if (allReadonly.value) return true;
    return comInsuranceKey.every((key) => isFieldReadonly(key));
});

const hideKeySet = computed(() => {
    const res = props.hideList;
    if (Array.isArray(res) && res.length) return new Set(res);
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return new Set(Object.keys(res).filter((key) => !!res[key]));
    }
    return new Set();
});
const isHidden = (key) => hideKeySet.value.has(key);

const resetData = () => {
    Object.assign(data, initialData);
    Object.assign(validationMessage, initialValidMessage);
    store.update(sessionKey, pickKeys(data, commonSyncKeys.value));
};

const resetCollision = () => {
    data.collision_insurance_age = '';
    data.collision_insurance_state = 'NON';
    data.collision_coverage_type = '';
    data.collision_insurance_coverage = '';
    validationMessage.collision_insurance_age = '';
    validationMessage.collision_insurance_state = '';
    validationMessage.collision_coverage_type = '';
    validationMessage.collision_insurance_coverage = '';
    store.update(sessionKey, pickKeys(data, commonSyncKeys.value));
};

const zeroIfEmptySet = new Set(comInsuranceKey);

const getData = () => {
    const payload = {};
    for (const key of allowedKeys) {
        if (isHidden(key)) continue;
        const val = data[key];
        payload[key] = zeroIfEmptySet.has(key) && val === '' ? 0 : val;
    }
    return payload;

    // const payload = {};
    // for (const key of allowedKeys) {
    //     if (!isHidden(key)) payload[key] = data[key];
    // }
    // return payload;
};

const collisionUnit = ref('');
const setInsuranceData = (newVal) => {
    if (!newVal) return;
    const payload = newVal.contract_data ?? newVal;

    Object.entries(payload).forEach(([key, val]) => {
        if (allowedKeys.includes(key)) {
            data[key] = val;
        }
    });
};

const checkRequired = () => {
    Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    let result = true;

    const keysToCheck = new Set(requiredKeys.value.filter((key) => allowedKeys.includes(key) && isEffectivelyRequired(key)));

    for (const key of keysToCheck) {
        const val = data[key];
        const isEmpty = val === '' || val === null || val === undefined;
        const isIdInvalid = key.endsWith('_id') && (isEmpty || Number(val) === 0);
        if (isEmpty || isIdInvalid) {
            if (key in validationMessage) validationMessage[key] = '필수입력입니다.';
            result = false;
        }
    }
    return result;
};

const setErrorMessage = (payload, reset = false) => {
    if (!payload) return;

    if (reset) {
        Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    }

    const collectPairs = (node, out = []) => {
        if (node == null) return out;

        if (Array.isArray(node)) {
            node.forEach((item) => collectPairs(item, out));
            return out;
        }

        if (typeof node === 'object') {
            for (const [k, v] of Object.entries(node)) {
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    collectPairs(v, out);
                } else {
                    out.push([k, v]);
                }
            }
            return out;
        }

        return out;
    };

    const pairs = collectPairs(payload);

    for (const [rawKey, rawVal] of pairs) {
        let key = String(rawKey).replace(/^contract_data\./, '');
        if (!(key in validationMessage)) continue;

        let msg = rawVal;
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ');
        if (msg == null) msg = '';

        validationMessage[key] = String(msg);
    }
};

function handleCollisionInsuranceCompleted(val) {
    data.collision_insurance_age = Number(val.collision_insurance_age);
    data.collision_insurance_state = val.collision_insurance_state;
    data.collision_coverage_type = val.collision_coverage_type;
    data.collision_insurance_coverage = val.collision_insurance_coverage;
    validationMessage.collision_insurance_state = '';
    validationMessage.collision_insurance_age = '';
    validationMessage.collision_coverage_type = '';
    validationMessage.collision_insurance_coverage = '';
}

function handleComInsuranceCompleted(val) {
    data.bodily_injury_coverage = val.bodily_injury_coverage;
    data.bodily_injury_excess = val.bodily_injury_excess;
    data.property_damage_coverage = val.property_damage_coverage;
    data.property_damage_excess = val.property_damage_excess;
    data.personal_injury_coverage = val.personal_injury_coverage;
    data.personal_death_coverage = val.personal_death_coverage;
    data.personal_injury_excess = val.personal_injury_excess;
    validationMessage.bodily_injury_coverage = '';
    validationMessage.bodily_injury_excess = '';
    validationMessage.property_damage_coverage = '';
    validationMessage.property_damage_excess = '';
    validationMessage.personal_injury_coverage = '';
    validationMessage.personal_death_coverage = '';
    validationMessage.personal_injury_excess = '';
}

const clearCollisionInfo = () => {
    data.collision_insurance_state = 'NON';
    data.collision_coverage_type = '';
    data.collision_insurance_coverage = '';
    data.collision_insurance_age = '';
    validationMessage.collision_insurance_state = '';
    validationMessage.collision_coverage_type = '';
    validationMessage.collision_insurance_coverage = '';
    validationMessage.collision_insurance_age = '';
};

const clearComInsuranceInfo = () => {
    data.bodily_injury_coverage = '';
    data.bodily_injury_excess = '';
    data.property_damage_coverage = '';
    data.property_damage_excess = '';
    data.personal_injury_coverage = '';
    data.personal_death_coverage = '';
    data.personal_injury_excess = '';
    validationMessage.bodily_injury_coverage = '';
    validationMessage.bodily_injury_excess = '';
    validationMessage.property_damage_coverage = '';
    validationMessage.property_damage_excess = '';
    validationMessage.personal_injury_coverage = '';
    validationMessage.personal_death_coverage = '';
    validationMessage.personal_injury_excess = '';
};

const isAgeReadOnly = computed(() => isFieldReadonly('collision_insurance_age'));
const isCoverageReadOnly = computed(() => isFieldReadonly('collision_coverage_type'));
const isCoverageAmountReadOnly = computed(() => isFieldReadonly('collision_insurance_coverage'));

onMounted(async () => {
    if (Object.keys(props.insuranceData).length === 0 && !props.nonComInsurance) {
        const val = await comInsurance.value?.getDefaultInsurance();
        handleComInsuranceCompleted(val);
    }
});

watch(
    () => data.collision_insurance_state,
    (newVal, oldVal) => {
        if (oldVal === undefined) return;
        if (newVal === 'NON' || newVal === '') {
            nextTick(() => {
                data.collision_insurance_state = 'NON';
                data.collision_coverage_type = '';
                data.collision_insurance_coverage = '';
                data.collision_insurance_age = '';
                validationMessage.collision_insurance_state = '';
                validationMessage.collision_coverage_type = '';
                validationMessage.collision_insurance_coverage = '';
                validationMessage.collision_insurance_age = '';
            });
        }
    },
    { immediate: true }
);

watch(
    () => data.collision_coverage_type,
    (newVal) => {
        if (newVal === '') {
            data.collision_insurance_coverage = '';
            validationMessage.collision_insurance_coverage = '';
            collisionUnit.value = '';
        } else {
            collisionUnit.value = newVal === 'R' ? '%' : '원';
        }
    },
    { immediate: true }
);

watch(
    () => props.contractType,
    (newVal, oldVal) => {
        if (newVal === 'SHORT' && newVal !== oldVal) {
            resetCollision(); //resetData();
        }
    }
);

watch(
    () => props.insuranceData,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') return;
        setInsuranceData(newVal);
    },
    { immediate: true, deep: false }
);

watch(
    () => props.readonlyList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isFieldReadonly(key)) validationMessage[key] = '';
        });
        // checkRequired();
    },
    { immediate: false, deep: false }
);

watch(
    () => props.hideList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isHidden(key)) validationMessage[key] = '';
        });
    },
    { immediate: false, deep: false }
);

watch(
    () => props.validMsg,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') {
            console.error('Invalid Error data');
            return;
        }
        setErrorMessage(newVal, true);
        // Object.keys(newVal).forEach((key) => {
        //     if (key.startsWith('contract_data.')) {
        //         const pureKey = key.replace('contract_data.', '');
        //         if (pureKey in validationMessage) {
        //             validationMessage[pureKey] = newVal[key];
        //         }
        //     } else {
        //         if (key in validationMessage) {
        //             validationMessage[key] = newVal[key];
        //         }
        //     }
        // });
    },
    { immediate: true }
);

watch(
    () => props.nonComInsurance,
    (on) => {
        if (on) clearComInsuranceInfo();
    },
    { immediate: true }
);

watch(
    () => commonSyncKeys.value.map((k) => data[k]),
    () => {
        if (pulling) return;
        pushing = true;
        store.update(sessionKey, pickKeys(data, commonSyncKeys.value));
        pushing = false;
    },
    { deep: false, immediate: true }
);

watch(
    () => commonSyncKeys.value.map((k) => sessionRef[k]),
    () => {
        if (pushing) return;
        pulling = true;
        commonSyncKeys.value.forEach((k) => {
            if (sessionRef[k] !== undefined && data[k] !== sessionRef[k]) {
                data[k] = sessionRef[k];
            }
        });
        pulling = false;
    },
    { deep: false, immediate: true }
);

const storeVehicleGroupId = computed(() => Number(sessionRef.vehicle_group_id ?? 0));
const showVehicleGroupMessage = ref(false);

const handleColInsuranceClick = () => {
    showVehicleGroupMessage.value = storeVehicleGroupId.value === 0;
};

watch(storeVehicleGroupId, (newValue) => {
    if (newValue > 0) showVehicleGroupMessage.value = false;
});

defineExpose({
    getData,
    resetData,
    setInsuranceData,
    checkRequired,
    setAllReadonly,
    setErrorMessage
});
</script>

<template>
    <div class="section-box base-text">
        <div class="section-body">
            <div class="section-left">
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <h2 class="section-title z-[-10] py-4">자차 보험 정보</h2>
                        <button v-if="!isColInsuranceActionDisabled" title="초기화 (선택안함)" @click="clearCollisionInfo()">
                            <i class="pi pi-undo text-[gray]"></i>
                        </button>
                    </div>

                    <div class="flex items-center justify-end gap-2">
                        <span v-if="showVehicleGroupMessage && storeVehicleGroupId === 0" class="validation-message">차량선택 후 자차보험을 선택해 주세요.</span>
                        <colinsurance
                            v-if="!isColInsuranceActionDisabled"
                            inputId="col_insurance"
                            :vehicleGroupId="storeVehicleGroupId"
                            :rentType="contractType"
                            :labelEmpty="true"
                            @click="handleColInsuranceClick"
                            @input="(payload) => handleCollisionInsuranceCompleted(payload)"
                        />
                    </div>
                </div>

                <div class="section-row">
                    <div class="input-box w-[50%]">
                        <combobox
                            inputId="collision_insurance_state"
                            v-model="data.collision_insurance_state"
                            :options="commonCode('COLLISION_INSURANCE_STATE').value"
                            :errmsg="validationMessage.collision_insurance_state"
                            label="자차 종류"
                            :placeholder="'자차 종류를 선택하세요.'"
                            :readonly="isFieldReadonly('collision_insurance_state')"
                            :required="isEffectivelyRequired('collision_insurance_state')"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <combobox
                            inputId="collision_insurance_age"
                            v-model="data.collision_insurance_age"
                            :options="commonCode('INSURANCE_AGE').value"
                            :errmsg="validationMessage.collision_insurance_age"
                            label="보험 가입 연령"
                            :placeholder="'보험 가입 연령을 선택하세요.'"
                            :readonly="isAgeReadOnly"
                            :required="isEffectivelyRequired('collision_insurance_age')"
                        />
                    </div>
                </div>
                <div class="section-row items-center">
                    <div class="input-box w-[50%]">
                        <combobox
                            inputId="collision_coverage_type"
                            v-model="data.collision_coverage_type"
                            :options="commonCode('COLLISION_COVERAGE_TYPE').value"
                            :errmsg="validationMessage.collision_coverage_type"
                            label="자차 한도"
                            :placeholder="'자차 한도 종류를 선택하세요.'"
                            :readonly="isCoverageReadOnly"
                            :required="isEffectivelyRequired('collision_coverage_type')"
                        />
                    </div>
                    <div class="input-box w-[50%]">
                        <inputbox
                            inputId="collision_insurance_coverage"
                            v-model="data.collision_insurance_coverage"
                            :number="'n'"
                            :placeholder="'자차 한도를 입력하세요.'"
                            :unit="collisionUnit"
                            :errmsg="validationMessage.collision_insurance_coverage"
                            :maxlength="6"
                            :readonly="isCoverageAmountReadOnly"
                            :required="isEffectivelyRequired('collision_insurance_coverage')"
                        />
                    </div>
                </div>
            </div>
            <div v-if="!nonComInsurance" class="section-right">
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <h2 class="section-title z-[-10] py-4">종합 보험 정보</h2>
                        <button v-if="!isComInsuranceActionDisabled" title="초기화" @click="clearComInsuranceInfo()">
                            <i class="pi pi-undo text-[gray]"></i>
                        </button>
                    </div>

                    <div class="flex justify-end">
                        <cominsurance ref="comInsurance" v-if="!isComInsuranceActionDisabled" inputId="com_insurance" :labelEmpty="true" @input="(payload) => handleComInsuranceCompleted(payload)" />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[60%]">
                        <injury
                            inputId="bodily_injury_coverage"
                            v-model="data.bodily_injury_coverage"
                            :errmsg="validationMessage.bodily_injury_coverage"
                            label="대인 한도"
                            :placeholder="'대인 한도를 입력하세요.'"
                            :readonly="isFieldReadonly('bodily_injury_coverage')"
                            :required="isEffectivelyRequired('bodily_injury_coverage')"
                        />
                    </div>
                    <div class="input-box w-[40%]">
                        <inputbox
                            inputId="bodily_injury_excess"
                            v-model="data.bodily_injury_excess"
                            :number="'n'"
                            :unit="'만원'"
                            :errmsg="validationMessage.bodily_injury_excess"
                            label="대인 면책금 (인당)"
                            :placeholder="'대인 면책금을 입력하세요.'"
                            :allowZero="true"
                            :maxlength="14"
                            :readonly="isFieldReadonly('bodily_injury_excess')"
                            :required="isEffectivelyRequired('bodily_injury_excess')"
                        />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[60%]">
                        <inputbox
                            inputId="property_damage_coverage"
                            v-model="data.property_damage_coverage"
                            :number="'n'"
                            :unit="'만원'"
                            :errmsg="validationMessage.property_damage_coverage"
                            label="대물 한도"
                            :placeholder="'대물 한도를 입력하세요.'"
                            :allowZero="true"
                            :maxlength="14"
                            :readonly="isFieldReadonly('property_damage_coverage')"
                            :required="isEffectivelyRequired('property_damage_coverage')"
                        />
                    </div>
                    <div class="input-box w-[40%]">
                        <inputbox
                            inputId="property_damage_excess"
                            v-model="data.property_damage_excess"
                            :number="'n'"
                            :unit="'만원'"
                            :errmsg="validationMessage.property_damage_excess"
                            label="대물 면책금 (인당)"
                            :placeholder="'대물 면책금을 입력하세요.'"
                            :allowZero="true"
                            :maxlength="14"
                            :readonly="isFieldReadonly('property_damage_excess')"
                            :required="isEffectivelyRequired('property_damage_excess')"
                        />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[30%]">
                        <inputbox
                            inputId="personal_injury_coverage"
                            v-model="data.personal_injury_coverage"
                            :number="'n'"
                            :unit="'만원'"
                            :errmsg="validationMessage.personal_injury_coverage"
                            label="자손 한도(부상)"
                            :placeholder="'자손 한도를 입력하세요.'"
                            :allowZero="true"
                            :maxlength="14"
                            :readonly="isFieldReadonly('personal_injury_coverage')"
                            :required="isEffectivelyRequired('personal_injury_coverage')"
                        />
                    </div>
                    <div class="input-box w-[30%]">
                        <inputbox
                            inputId="personal_death_coverage"
                            v-model="data.personal_death_coverage"
                            :number="'n'"
                            :unit="'만원'"
                            :errmsg="validationMessage.personal_death_coverage"
                            label="자손 한도(사망)"
                            :placeholder="'자손 한도를 입력하세요.'"
                            :allowZero="true"
                            :maxlength="14"
                            :readonly="isFieldReadonly('personal_death_coverage')"
                            :required="isEffectivelyRequired('personal_death_coverage')"
                        />
                    </div>
                    <div class="input-box w-[40%]">
                        <inputbox
                            inputId="personal_injury_excess"
                            v-model="data.personal_injury_excess"
                            :number="'n'"
                            :unit="'만원'"
                            :errmsg="validationMessage.personal_injury_excess"
                            label="자손 면책금"
                            :placeholder="'자손 면책금을 입력하세요.'"
                            :allowZero="true"
                            :maxlength="14"
                            :readonly="isFieldReadonly('personal_injury_excess')"
                            :required="isEffectivelyRequired('personal_injury_excess')"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style src="@/assets/css/form-common.css"></style>
<style scoped>
.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
